import SearchIcon from '@mui/icons-material/Search';
import {
  AutocompleteRenderOptionState,
  Box,
  Button,
  GridLegacy as Grid,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DateRange,
  DateRangePicker,
  LocalizationProvider,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { enGB } from 'date-fns/locale';
import { useAnalytics } from 'providers/AnalyticsProvider';
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useRouter } from '../../hooks/useRouter';
import {
  Location,
  useGetBoatsLocationsQuery,
  useGetCategoriesQuery,
  useGetExperiencesLocationsQuery,
  useGetPropertiesLocationsQuery,
} from '../../redux/ellipsisApi';
import { useAppDispatch, useHomeSearch } from '../../redux/hooks';
import ui from '../../redux/reducers/uiReducer';
import { styles } from '../../theme/theme';
import AutocompleteOption from '../ui/AutocompleteOption';

const StyledTab = styled(Tab)(({ theme }) => ({
  padding: '8px 20px',
  fontSize: 16,
  [theme.breakpoints.down('sm')]: {
    fontSize: 13,
  },
}));

interface OwnProps {
  handleSearchChange: React.Dispatch<SetStateAction<any>>;
}

const HomeSearch: React.FC<OwnProps> = (props) => {
  const analytics = useAnalytics();
  const router = useRouter();

  const { data: boatCategories } = useGetCategoriesQuery({
    bookableTypes: JSON.stringify('Boat'),
  });
  const { data: experienceCategories } = useGetCategoriesQuery({
    bookableTypes: JSON.stringify('Experience'),
  });
  const { data: propertyCategories } = useGetCategoriesQuery({
    bookableTypes: JSON.stringify('Property'),
  });
  const { data: boatLocations, isLoading: isBoatLocLoading } =
    useGetBoatsLocationsQuery();
  const { data: experienceLocations, isLoading: isExperienceLocLoading } =
    useGetExperiencesLocationsQuery();
  const { data: propertyLocations, isLoading: isPropertyLocLoading } =
    useGetPropertiesLocationsQuery();
  const listingUrls = ['boats', 'experiences', 'properties'];

  const { homeSearch } = useHomeSearch();
  const [selectedSearch, setSelectedSearch] = useState<number>(
    homeSearch?.selectedTab ?? 3
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<string[]>(
    homeSearch?.category ?? ['', '', '']
  );
  const [selectedLocation, setSelectedLocation] = useState<(Location | null)[]>(
    homeSearch?.location ?? [null, null, null]
  );
  const [selectedDate, setSelectedDate] = React.useState<DateRange<Date>>(
    homeSearch?.date ?? [null, null]
  );

  const dispatch = useAppDispatch();

  const listingTypes = useMemo(
    () => [
      boatCategories?.map((cat) => ({ label: cat.type, value: cat.id })),
      experienceCategories?.map((cat) => ({ label: cat.type, value: cat.id })),
      propertyCategories?.map((cat) => ({ label: cat.type, value: cat.id })),
    ],
    [boatCategories, experienceCategories, propertyCategories]
  );

  const { handleSearchChange } = props;
  const changeSearch = useCallback(
    (newValue: number) => {
      const searchTypes = ['Boat', 'Experience', 'Property', ''];
      if (handleSearchChange) {
        handleSearchChange(searchTypes[newValue]);
      }
      setSelectedSearch(newValue);
      const newHomeSearch = { ...homeSearch };
      newHomeSearch.selectedTab = newValue;
      dispatch(ui.actions.setHomeSearchStatus(newHomeSearch));
    },
    [handleSearchChange, homeSearch, dispatch]
  );

  useEffect(() => {
    changeSearch(selectedSearch);
    if (typeof window !== 'undefined') {
      const eventName = `Click search`;
      analytics.trackEvent(eventName);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = (index: number) => {
    // Clicking the already-selected tab deselects it and returns to the
    // "all" state (3), matching the initial unfiltered view.
    changeSearch(selectedSearch === index ? 3 : index);
  };

  const handleSelectedCategoryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newCategories = [...selectedCategoryId];
    newCategories[selectedSearch] = event.target.value;
    setSelectedCategoryId(newCategories);
    const newHomeSearch = { ...homeSearch };
    newHomeSearch.category = newCategories;
    dispatch(ui.actions.setHomeSearchStatus(newHomeSearch));
  };
  const handleSelectLocationChange = (
    event: React.SyntheticEvent,
    value: any
  ) => {
    const newLocations = [...selectedLocation];
    newLocations[selectedSearch] = value;
    setSelectedLocation(newLocations);
    const newHomeSearch = { ...homeSearch };
    newHomeSearch.location = newLocations;
    dispatch(ui.actions.setHomeSearchStatus(newHomeSearch));
  };
  const locQs = selectedLocation[selectedSearch]
    ? `location.division=${selectedLocation[selectedSearch]?.division}&`
    : '';
  const catQs = selectedCategoryId[selectedSearch]
    ? `categoryId=${selectedCategoryId[selectedSearch]}&`
    : '';
  const startDateQs =
    selectedDate && Array.isArray(selectedDate) && selectedDate[0]
      ? `startDate=${selectedDate[0]?.toISOString()}&`
      : '';
  const endDateQs =
    selectedDate && Array.isArray(selectedDate) && selectedDate[1]
      ? `endDate=${selectedDate[1]?.toISOString()}&`
      : '';
  const qs =
    catQs || locQs || startDateQs || endDateQs
      ? `?${catQs}${locQs}${startDateQs}${endDateQs}`.replace(/&$/, '')
      : '';
  const dedupedBoatLocations: Location[] = useMemo(
    () =>
      boatLocations?.filter(
        (entry: any, index: number, self: any) =>
          index === self.findIndex((e: any) => e.division === entry.division)
      ) ?? [],
    [boatLocations]
  );
  const dedupedExperienceLocations: Location[] = useMemo(
    () =>
      experienceLocations?.filter(
        (entry: any, index: number, self: any) =>
          index === self.findIndex((e: any) => e.division === entry.division)
      ) ?? [],
    [experienceLocations]
  );
  const dedupedPropertyLocations: Location[] = useMemo(
    () =>
      propertyLocations?.filter(
        (entry: any, index: number, self: any) =>
          index === self.findIndex((e: any) => e.division === entry.division)
      ) ?? [],
    [propertyLocations]
  );

  const autocompleteOptions = useMemo(() => {
    if (selectedSearch === 3) {
      return dedupedBoatLocations;
    }
    const loadingByIndex = [
      isBoatLocLoading,
      isExperienceLocLoading,
      isPropertyLocLoading,
    ];
    if (loadingByIndex[selectedSearch]) {
      return [];
    }
    return (
      [
        dedupedBoatLocations,
        dedupedExperienceLocations,
        dedupedPropertyLocations,
      ][selectedSearch] ?? []
    );
  }, [
    selectedSearch,
    isBoatLocLoading,
    isExperienceLocLoading,
    isPropertyLocLoading,
    dedupedBoatLocations,
    dedupedExperienceLocations,
    dedupedPropertyLocations,
  ]);

  return (
    <Box>
      <Tabs
        value={
          selectedSearch >= 0 && selectedSearch <= 2 ? selectedSearch : false
        }
        indicatorColor="primary"
        textColor="inherit"
        variant="fullWidth"
      >
        <StyledTab label="Boats" onClick={() => handleTabClick(0)} />
        <StyledTab label="Experiences" onClick={() => handleTabClick(1)} />
        <StyledTab label="Stays" onClick={() => handleTabClick(2)} />
      </Tabs>
      <Box sx={{ padding: '10px' }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Autocomplete<Location, false, false, false>
              options={autocompleteOptions as Location[]}
              fullWidth
              value={selectedLocation[selectedSearch] ?? null}
              isOptionEqualToValue={(option: Location, value: Location) => {
                return option.division === value.division;
              }}
              getOptionLabel={(option: Location) => {
                return option?.division || '';
              }}
              onChange={handleSelectLocationChange}
              // Grouping by region is not available on our simplified option shape
              // groupBy can be reintroduced when API adds a stable region field
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: Location,
                state: AutocompleteRenderOptionState
              ) => {
                return (
                  <AutocompleteOption
                    label={option.division}
                    selected={state.selected}
                    {...props}
                  />
                );
              }}
              renderInput={(params: any) => {
                return (
                  <TextField
                    {...params}
                    id="select-location"
                    data-cy="home-search-input"
                    variant={styles.textFieldVariant}
                    label="Where"
                    inputProps={{ ...params.inputProps, inputMode: 'none' }}
                  />
                );
              }}
            />
          </Grid>

          {selectedSearch !== 1 && (
            <Grid item xs={7} lg={4}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={enGB}
              >
                <DateRangePicker
                  value={selectedDate}
                  disablePast
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                    const newHomeSearch = { ...homeSearch };
                    newHomeSearch.date = newValue;
                    dispatch(ui.actions.setHomeSearchStatus(newHomeSearch));
                  }}
                  slots={{ field: SingleInputDateRangeField }}
                  slotProps={{
                    field: {
                      shouldRespectLeadingZeros: true,
                      format: 'd MMM',
                      separator: ' - ',
                      placeholder: 'Add dates',
                    } as any,
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}
          <Grid
            item
            xs={selectedSearch === 1 ? 12 : 5}
            lg={selectedSearch === 1 ? 8 : 4}
          >
            <TextField
              id="select-listable-type"
              select
              value={selectedCategoryId[selectedSearch] || ''}
              sx={{ width: '100%' }}
              label="Type"
              onChange={handleSelectedCategoryChange}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                native: true,
              }}
            >
              <option key="All" value="">
                All
              </option>
              {listingTypes[selectedSearch]?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Button
              variant="contained"
              sx={{ width: '100%' }}
              startIcon={<SearchIcon />}
              onClick={() => {
                const eventName = 'Click search';
                analytics.trackEvent(eventName);
                router.push(`/${listingUrls[selectedSearch] ?? 'boats'}${qs}`);
              }}
              data-cy="home-search-button"
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default HomeSearch;
